import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  Clock,
  CheckCheck,
  Check,
  Loader2,
  AlertCircle
} from 'lucide-react';
import MessageBubble from '../MessageBubble/MessageBubble';
import { getMessages, markAllAsRead } from '../../../services/chatService';
import '../Chat.scss';

const ChatMessages = ({ conversation, userId, socket }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const isFirstLoad = useRef(true);

  // ─── Carregar mensagens ──────────────────────────────────────────────
  const loadMessages = useCallback(async (pageNum = 1) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const data = await getMessages(conversation.id, pageNum);

      if (pageNum === 1) {
        setMessages(data.messages);
        setHasMore(data.hasMore);
      } else {
        setMessages(prev => [...data.messages, ...prev]);
        setHasMore(data.hasMore);
      }

      setPage(pageNum);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [conversation.id]);

  // ─── Marcar como lida ─────────────────────────────────────────────────
  const markAsRead = useCallback(async () => {
    try {
      await markAllAsRead(conversation.id);
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  }, [conversation.id]);

  // ─── Efeitos ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (conversation.id) {
      loadMessages(1);
      markAsRead();
    }
  }, [conversation.id]);

  // ─── Scroll para o final ─────────────────────────────────────────────
  useEffect(() => {
    if (!loading && messages.length > 0 && isFirstLoad.current) {
      scrollToBottom();
      isFirstLoad.current = false;
    }
  }, [messages, loading]);

  // ─── Socket para novas mensagens ─────────────────────────────────────

  // ChatMessages.jsx - Substitua o useEffect do socket

  useEffect(() => {
    if (socket) {
      // console.log('💬 ChatMessages - Socket recebido:', socket.state);
      // console.log('💬 ChatMessages - Handlers atuais:', socket._handlers);

      const handleNewMessage = (message) => {
        // console.log('📩 ChatMessages - new_message:', message);
        if (message.conversationId === conversation.id) {
          setMessages(prev => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
          });

          if (message.senderId !== userId) {
            markAsRead();
          }

          setTimeout(scrollToBottom, 100);
        }
      };

      // 🔥 HANDLER SEPARADO para message_sent
      const handleMessageSent = (message) => {
        // console.log('✅ ChatMessages - message_sent:', message);
        if (message.conversationId === conversation.id) {
          setMessages(prev => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
          });
          setTimeout(scrollToBottom, 100);
        }
      };

      socket.on('new_message', handleNewMessage);
      socket.on('message_sent', handleMessageSent); // 👈 USA HANDLER SEPARADO
      // console.log('💬 ChatMessages - Handlers após registrar:', socket._handlers);
      return () => {
        socket.off('new_message', handleNewMessage);
        socket.off('message_sent', handleMessageSent);
      };
    }
  }, [socket, conversation.id, userId, markAsRead]);

  // ─── Funções de scroll ───────────────────────────────────────────────
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && hasMore && !loadingMore) {
      loadMessages(page + 1);
    }
  };

  // ─── Separar mensagens por data ─────────────────────────────────────
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const date = new Date(msg.createdAt).toLocaleDateString('pt-BR');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="chat-messages__loading">
        <Loader2 size={24} className="chat-messages__spinner" />
        <span>Carregando mensagens...</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="chat-messages__empty">
        <MessageCircle size={48} />
        <h4>Nenhuma mensagem ainda</h4>
        <p>Seja o primeiro a enviar uma mensagem!</p>
      </div>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <div
      className="chat-messages"
      ref={containerRef}
      onScroll={handleScroll}
    >
      {loadingMore && (
        <div className="chat-messages__loader-more">
          <Loader2 size={18} className="chat-messages__spinner" />
          Carregando mensagens anteriores...
        </div>
      )}

      <div className="chat-messages__container">
        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date}>
            <div className="chat-messages__date-divider">
              <span>{date}</span>
            </div>

            {msgs.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderId === userId}
                showAvatar={true}
              />
            ))}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatMessages;